/**
 * One rule, three screens: inventory still sitting in a railcar that has left
 * the yard cannot be picked onto an outbound.
 *
 * Releasing a rail inbound (or pulling the car in Track Management) is the
 * car leaving. Bulk stored in the car is shipped out by that release, but
 * unit inventory that was never unloaded stays STORED on the track area with
 * nothing physical behind it — and a pick against it sends the crew to a
 * location that no longer exists (the TrackCar is gone, so the item's
 * location resolves to nothing and the pick page cannot even render).
 *
 * Three places can put such inventory on a pick list, and all three read the
 * rule from here so they cannot drift apart:
 *
 *   - the pick-list loader (OutboundUtils.getAllInventories), which drops the
 *     items from the search dialog (excludeReleasedRailcarInventories)
 *   - the outbound dispatch save and the pick-list item save, which re-check
 *     against a fresh read because the loaded list may predate the release
 *     (OutboundUtils.validateReleasedRailcarCommit)
 *
 * Only inventory still on the track is affected. Unit inventory unloaded into
 * the yard from a car that later left is ordinary stock.
 */
import { CarrierType, InboundStatus } from "./constants";
import { isTrack } from "./locationUtils";

/**
 * The inbound's railcar has left: released, or pulled in Track Management.
 * `collectedDateTime` is checked alongside `isCollected` because some queries
 * select only one of the two.
 */
export const isReleasedRailcar = (inbound) =>
  Boolean(inbound) &&
  inbound.carrierType === CarrierType.RAIL &&
  (inbound.status === InboundStatus.RELEASED ||
    Boolean(inbound.collectedDateTime) ||
    inbound.isCollected === true);

// An item is "in the car" when its area is a track. The item's own areaId is
// canonical; the parent inventory's area is only consulted for an item that
// carries none (aggregate rows written before items had an area).
const isOnTrack = (inventory, item) => {
  if (item?.areaId) return isTrack(item.areaId);
  if (item?.location?.area) return isTrack(item.location.area);
  return isTrack(inventory?.areaId);
};

export const isInventoryItemOnReleasedRailcar = (inventory, item) =>
  isReleasedRailcar(inventory?.inboundShipment) && isOnTrack(inventory, item);

/**
 * The pick-list loader's view of an inventory list: items still in a released
 * car are dropped, and an inventory whose every placed item was in the car
 * goes with them. An inventory with no items at all is unplaced, not gone —
 * it stays, as it does for the damaged-stock filter.
 */
export const excludeReleasedRailcarInventories = (inventoryList) =>
  (inventoryList || []).reduce((acc, inventory) => {
    const items = inventory?.inventoryItems?.items || [];
    if (!isReleasedRailcar(inventory?.inboundShipment)) {
      acc.push(inventory);
      return acc;
    }
    const keptItems = items.filter(
      (item) => !isInventoryItemOnReleasedRailcar(inventory, item),
    );
    if (items.length > 0 && keptItems.length === 0) return acc;
    acc.push({
      ...inventory,
      inventoryItems: { ...inventory.inventoryItems, items: keptItems },
    });
    return acc;
  }, []);

/**
 * What to call the car in a refusal. The inbound's railCarNumber is the
 * number on the car; the inventory's is a copy of it, and the lot is the last
 * resort for an inventory that never recorded one.
 */
export const releasedRailcarLabel = (inventory) =>
  inventory?.inboundShipment?.railCarNumber ||
  inventory?.railCarNumber ||
  [inventory?.productName, inventory?.lotNumber].filter(Boolean).join(" ") ||
  "this railcar";

export const releasedRailcarCommitRefusal = (labels) => {
  const cars = [...new Set((labels || []).filter(Boolean))];
  if (!cars.length) return null;
  const subject =
    cars.length === 1
      ? `Railcar ${cars[0]} has been released`
      : `Railcars ${cars.join(", ")} have been released`;
  return (
    `${subject} — inventory still on the car can no longer be picked. ` +
    "Remove it from the pick list to save."
  );
};
